/**
 * Comprehensive ISO 4217 currency list generated via the browser's Intl API.
 * No external dependency — uses Intl.DisplayNames for names and
 * Intl.NumberFormat for narrow symbols.
 */

const ALL_CODES = [
  'AED','AFN','ALL','AMD','ANG','AOA','ARS','AUD','AWG','AZN',
  'BAM','BBD','BDT','BGN','BHD','BIF','BMD','BND','BOB','BRL',
  'BSD','BTN','BWP','BYN','BZD','CAD','CDF','CHF','CLP','CNY',
  'COP','CRC','CUP','CVE','CZK','DJF','DKK','DOP','DZD','EGP',
  'ERN','ETB','EUR','FJD','FKP','GBP','GEL','GHS','GIP','GMD',
  'GNF','GTQ','GYD','HKD','HNL','HTG','HUF','IDR','ILS','INR',
  'IQD','IRR','ISK','JMD','JOD','JPY','KES','KGS','KHR','KMF',
  'KPW','KRW','KWD','KYD','KZT','LAK','LBP','LKR','LRD','LSL',
  'LYD','MAD','MDL','MGA','MKD','MMK','MNT','MOP','MRU','MUR',
  'MVR','MWK','MXN','MYR','MZN','NAD','NGN','NIO','NOK','NPR',
  'NZD','OMR','PAB','PEN','PGK','PHP','PKR','PLN','PYG','QAR',
  'RON','RSD','RUB','RWF','SAR','SBD','SCR','SDG','SEK','SGD',
  'SHP','SLE','SOS','SRD','STN','SVC','SYP','SZL','THB','TJS',
  'TMT','TND','TOP','TRY','TTD','TWD','TZS','UAH','UGX','USD',
  'UYU','UZS','VES','VND','VUV','WST','XAF','XCD','XOF','XPF',
  'YER','ZAR','ZMW',
];

// Reasonable default locale for common currencies (used in Settings → auto-set locale)
export const CURRENCY_LOCALE_MAP = {
  ILS: 'he-IL', USD: 'en-US', EUR: 'de-DE', GBP: 'en-GB',
  JPY: 'ja-JP', CAD: 'en-CA', AUD: 'en-AU', CHF: 'de-CH',
  CNY: 'zh-CN', INR: 'en-IN', KRW: 'ko-KR', BRL: 'pt-BR',
  MXN: 'es-MX', RUB: 'ru-RU', SEK: 'sv-SE', NOK: 'nb-NO',
  DKK: 'da-DK', PLN: 'pl-PL', CZK: 'cs-CZ', HUF: 'hu-HU',
  TRY: 'tr-TR', AED: 'ar-AE', SAR: 'ar-SA', SGD: 'en-SG',
  HKD: 'zh-HK', NZD: 'en-NZ', ZAR: 'en-ZA', THB: 'th-TH',
  IDR: 'id-ID', MYR: 'ms-MY', PHP: 'en-PH', TWD: 'zh-TW',
};

function getSymbol(code) {
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(1);
    return parts.find(p => p.type === 'currency')?.value || code;
  } catch {
    return code;
  }
}

let _cache = null;

/** Returns all supported ISO 4217 currencies sorted by name. */
export function getAllCurrencies() {
  if (_cache) return _cache;
  let dn;
  try { dn = new Intl.DisplayNames(['en'], { type: 'currency' }); } catch { dn = null; }

  _cache = ALL_CODES
    .map(code => {
      try {
        const name   = dn ? dn.of(code) : code;
        const symbol = getSymbol(code);
        return { code, name, symbol };
      } catch {
        return null;
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  return _cache;
}

/** Look up a single currency by code. Falls back to { code, name: code, symbol: code }. */
export function getCurrencyInfo(code) {
  if (!code) return { code: '', name: '', symbol: '' };
  return getAllCurrencies().find(c => c.code === code)
    || { code, name: code, symbol: getSymbol(code) };
}
